import classNames from 'classnames';
import React from 'react';

import { useForm, FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import Button from 'reactstrap/lib/Button';
import Form from 'reactstrap/lib/Form';
import FormGroup from 'reactstrap/lib/FormGroup';
import Spinner from 'reactstrap/lib/Spinner';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';

import { NewsletterSubscriber, unsubscribe } from '@ttstr/api';

import { EMAIL_PATTERN } from '../../utils';

import FormField from '../Form/FormField';
import ValidationAlert from '../Form/ValidationAlert';

const defaultInitialValues: NewsletterSubscriber = {
  email: '',
  locale: 'de',
};

interface OwnProps {
  onUnsubscribe?(): void;
}

type Props = Readonly<OwnProps>;

const NewsletterUnsubscribe: React.FC<Props> = ({ onUnsubscribe }) => {
  const [signupError, setSignupError] = React.useState<string>(null);
  const { t } = useTranslation();
  const formContext = useForm<NewsletterSubscriber>({
    mode: 'onChange',
    defaultValues: defaultInitialValues,
  });
  const { handleSubmit, reset, formState } = formContext;
  const { isValid, isSubmitting } = formState;

  const onSubmit = async (values: NewsletterSubscriber) => {
    if ('tnc' in values) {
      delete values.tnc;
    }

    // hardcoding locale to 'de'. Not nice, but locale is required for the api.
    // The value for locale isn't used for anything right now.
    values.locale = 'de';

    try {
      setSignupError(null);
      await unsubscribe(values.email);
      if (onUnsubscribe) onUnsubscribe();
    } catch (error) {
      setSignupError(error.message);
    }
  };

  const handleReset = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    reset();
  };

  return (
    <section>
      <FormProvider {...formContext}>
        <Form onSubmit={handleSubmit(onSubmit)} onReset={handleReset}>
          <fieldset disabled={isSubmitting}>
            <FormGroup tag="fieldset" className="mb-0">
              <legend>{t(`CUSTOMER.DATA`)}</legend>
              <Row>
                <Col>
                  {/* Email */}
                  <FormField
                    type="email"
                    label={t('CUSTOMER.EMAIL')}
                    name="email"
                    helpText=""
                    autoComplete="billing email"
                    validationOptions={{
                      required: true,
                      validate: {
                        'STRING.EMAIL': (value) => EMAIL_PATTERN.test(value),
                      },
                    }}
                  />
                </Col>
              </Row>
            </FormGroup>
            <hr />

            <ValidationAlert className="mb-2" />

            {signupError && <p className="text-danger">{t('NEWSLETTER.FAILED')}</p>}

            <FormGroup className="d-flex flex-column flex-md-row-reverse justify-content-md-between align-items-md-center">
              <Button type="submit" color="primary" size="lg" className="mb-3" disabled={!isValid}>
                {t('NEWSLETTER.UNSUBSCRIBE.ACTION')}
                <span className={classNames('spinner-wrapper', { invisible: !isSubmitting })}>
                  <Spinner color="primary" size="sm" />
                </span>
              </Button>
              <Button type="reset" color="link" className="mb-3">
                {t('CUSTOMER.RESET')}
              </Button>
            </FormGroup>
          </fieldset>
        </Form>
      </FormProvider>
    </section>
  );
};

export default React.memo(NewsletterUnsubscribe);
